import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { TrendingUp, TrendingDown, Calendar } from "lucide-react";
import { carbonInventoryService } from "../../services";
import { useTran } from "@/hooks";

const CarbonTrendAnalysis = ({ currentYear }) => {
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const { t } = useTran();

  useEffect(() => {
    loadTrendData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentYear]);

  const loadTrendData = async () => {
    setLoading(true);
    try {
      const years = [currentYear - 2, currentYear - 1, currentYear];
      const promises = years.map(async (year) => {
        try {
          const [targetsRes, overviewRes] = await Promise.all([
            carbonInventoryService.getCarbonTargets(),
            carbonInventoryService.getCarbonBankOverview(year, "approved"),
          ]);

          const targetsData = targetsRes;
          let yearTarget = null;
          if (Array.isArray(targetsData?.targets)) {
            yearTarget = targetsData.targets.find((t) => t.year === year);
          } else if (targetsData?.year === year) {
            yearTarget = targetsData;
          }
          const overview = overviewRes;

          return {
            year,
            scope1Target: Object.values(yearTarget?.scope1 || {}).reduce(
              (sum, v) => sum + Number(v || 0),
              0,
            ),
            scope1Actual: overview?.aggregated?.scope1TotalTco2e || 0,
            scope2Target: Object.values(yearTarget?.scope2 || {}).reduce(
              (sum, v) => sum + Number(v || 0),
              0,
            ),
            scope2Actual: overview?.aggregated?.scope2TotalTco2e || 0,
          };
        } catch (err) {
          return {
            year,
            scope1Target: 0,
            scope1Actual: 0,
            scope2Target: 0,
            scope2Actual: 0,
          };
        }
      });

      const results = await Promise.all(promises);
      setTrendData(results);

      // Calculate summary statistics
      if (results.length >= 2) {
        const current = results[results.length - 1];
        const previous = results[results.length - 2];

        const scope1Change = current.scope1Actual - previous.scope1Actual;
        const scope2Change = current.scope2Actual - previous.scope2Actual;
        const scope1PercentChange =
          previous.scope1Actual > 0
            ? ((scope1Change / previous.scope1Actual) * 100).toFixed(1)
            : 0;
        const scope2PercentChange =
          previous.scope2Actual > 0
            ? ((scope2Change / previous.scope2Actual) * 100).toFixed(1)
            : 0;

        setSummary({
          scope1Change,
          scope2Change,
          scope1PercentChange,
          scope2PercentChange,
        });
      }
    } catch (err) {
      console.error("Failed to load trend data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-lg dark:border-slate-700 dark:bg-slate-800">
          <p className="mb-2 font-semibold text-slate-900 dark:text-white">
            {label}
          </p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value.toFixed(2)} {"tCO₂e"}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Card className="border-slate-200 dark:border-slate-800">
        <CardContent className="p-12 text-center">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Loading trend data...
          </p>
        </CardContent>
      </Card>
    );
  }

  if (trendData.length === 0) {
    return null;
  }

  return (
    <Card className="border-slate-200 dark:border-slate-800">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          {t("Year-over-Year Trend Analysis")}
        </CardTitle>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t(
            `Historical performance across the last ${trendData.length} years`,
          )}
        </p>
      </CardHeader>
      <CardContent>
        {/* Summary Cards */}
        {summary && (
          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    {t("Scope 1 YoY Change")}
                  </p>
                  <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
                    {summary.scope1PercentChange}%
                  </p>
                </div>
                <div
                  className={`flex items-center gap-1 ${
                    summary.scope1Change > 0
                      ? "text-red-600 dark:text-red-400"
                      : "text-green-600 dark:text-green-400"
                  }`}
                >
                  {summary.scope1Change > 0 ? (
                    <TrendingUp className="h-5 w-5" />
                  ) : (
                    <TrendingDown className="h-5 w-5" />
                  )}
                  <span className="text-sm font-medium">
                    {Math.abs(summary.scope1Change).toFixed(2)} tCO₂e
                  </span>
                </div>
              </div>
              <Badge
                className={`mt-2 ${
                  summary.scope1Change > 0
                    ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                    : "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                }`}
              >
                {summary.scope1Change > 0 ? "Increased" : "Decreased"} vs last
                year
              </Badge>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    {t("Scope 2 YoY Change")}
                  </p>
                  <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
                    {summary.scope2PercentChange}%
                  </p>
                </div>
                <div
                  className={`flex items-center gap-1 ${
                    summary.scope2Change > 0
                      ? "text-red-600 dark:text-red-400"
                      : "text-green-600 dark:text-green-400"
                  }`}
                >
                  {summary.scope2Change > 0 ? (
                    <TrendingUp className="h-5 w-5" />
                  ) : (
                    <TrendingDown className="h-5 w-5" />
                  )}
                  <span className="text-sm font-medium">
                    {Math.abs(summary.scope2Change).toFixed(2)} tCO₂e
                  </span>
                </div>
              </div>
              <Badge
                className={`mt-2 ${
                  summary.scope2Change > 0
                    ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                    : "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                }`}
              >
                {summary.scope2Change > 0 ? "Increased" : "Decreased"} vs last
                year
              </Badge>
            </div>
          </div>
        )}

        {/* Line Chart */}
        <ResponsiveContainer width="100%" height={350}>
          <LineChart
            data={trendData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              className="stroke-slate-200 dark:stroke-slate-700"
            />
            <XAxis
              dataKey="year"
              tick={{ fill: "currentColor" }}
              className="text-slate-600 dark:text-slate-400"
            />
            <YAxis
              tick={{ fill: "currentColor" }}
              className="text-slate-600 dark:text-slate-400"
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ paddingTop: "20px" }} />
            <Line
              type="monotone"
              dataKey="scope1Target"
              stroke="#94a3b8"
              strokeDasharray="5 5"
              name="Scope 1 Target"
              dot={{ r: 4 }}
              strokeWidth={2}
            />
            <Line
              type="monotone"
              dataKey="scope1Actual"
              stroke="#10b981"
              name="Scope 1 Actual"
              dot={{ r: 5 }}
              strokeWidth={2}
            />
            <Line
              type="monotone"
              dataKey="scope2Target"
              stroke="#cbd5e1"
              strokeDasharray="5 5"
              name="Scope 2 Target"
              dot={{ r: 4 }}
              strokeWidth={2}
            />
            <Line
              type="monotone"
              dataKey="scope2Actual"
              stroke="#3b82f6"
              name="Scope 2 Actual"
              dot={{ r: 5 }}
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default CarbonTrendAnalysis;
